// Calendar Component
import { Button } from "../button";
import { ReactNode, useEffect, useMemo, useState } from "react";
import {isSameDay, isWithinInterval} from "date-fns";
import { DateRange } from "./rangePicker";
import {cn, getDaysInMonth, getFirstDayOfMonth} from "../../lib/fns";
import {LeftOutlined} from "../../assets/svgs/LeftOutlined";
import {RightOutlined} from "../../assets/svgs/RightOutlined";
import {DoubleRightOutlined} from "../../assets/svgs/DoubleRightOutlined";
import {DoubleLeftOutlined} from "../../assets/svgs/DoubleLeftOutlined";

export const monthNames = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
]

export const dayNames = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"]

const today = new Date();

export function CalendarPanel({
                                rangeStart,
                                rangeEnd,
                                onSelect,
                                onHover,
                                selectingEnd,
                                timePicker
                              }: {
  rangeStart: Date | null
  rangeEnd: Date | null
  onSelect: (range: DateRange) => void
  selectingEnd?: boolean
  onHover: (range: DateRange) => void
  timePicker: ReactNode
}) {
  const [hoveredDate, setHoveredDate] = useState<Date | null>(null)
  const [currentMonth, setCurrentMonth] = useState(
    rangeStart?.getMonth() ?? today.getMonth()
  )
  const [currentYear, setCurrentYear] = useState(
    rangeStart?.getFullYear() ?? today.getFullYear()
  )
  const daysInMonth = getDaysInMonth(currentYear, currentMonth)
  const firstDay = getFirstDayOfMonth(currentYear, currentMonth)
  const prevMonthDays = getDaysInMonth(currentYear, currentMonth - 1)

  const days: { date: Date; isCurrentMonth: boolean }[] = []

  // Previous month days
  for (let i = firstDay - 1; i >= 0; i--) {
    const date = new Date(currentYear, currentMonth - 1, prevMonthDays - i)
    days.push({ date, isCurrentMonth: false })
  }

  // Current month days
  for (let i = 1; i <= daysInMonth; i++) {
    const date = new Date(currentYear, currentMonth, i)
    days.push({ date, isCurrentMonth: true })
  }

  // Next month days
  const remainingDays = 42 - days.length
  for (let i = 1; i <= remainingDays; i++) {
    const date = new Date(currentYear, currentMonth + 1, i)
    days.push({ date, isCurrentMonth: false })
  }


  const handlePrevYear = () => {
    setCurrentYear(currentYear - 1)
  }

  const handleNextYear = () => {
    setCurrentYear(currentYear + 1)
  }

  const handlePrevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11)
      setCurrentYear(currentYear - 1)
    } else {
      setCurrentMonth(currentMonth - 1)
    }
  }

  const handleNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0)
      setCurrentYear(currentYear + 1)
    } else {
      setCurrentMonth(currentMonth + 1)
    }
  }

  const effectiveStart = useMemo(() => {
    if (hoveredDate) {
      if (!selectingEnd && rangeEnd && !isSameDay(hoveredDate, rangeEnd)) {
        if (hoveredDate < rangeEnd) return hoveredDate
        if (hoveredDate > rangeEnd) return rangeEnd
      }
      if (selectingEnd && rangeStart && !isSameDay(hoveredDate, rangeStart) && hoveredDate < rangeStart) return hoveredDate
    }
    if (rangeStart) return rangeStart
    return new Date();
  }, [hoveredDate, rangeEnd, rangeStart, selectingEnd]);

  const effectiveEnd = useMemo(() => {
    if (hoveredDate) {
      if (!selectingEnd && rangeEnd && !isSameDay(hoveredDate, rangeEnd) && rangeEnd < hoveredDate) return hoveredDate
      if (selectingEnd && rangeStart && !isSameDay(hoveredDate, rangeStart)) {
        if (rangeStart < hoveredDate) return hoveredDate
        if (rangeStart > hoveredDate) return rangeStart
      }
    }
    if (rangeEnd) return rangeEnd
    return new Date();
  }, [hoveredDate, rangeEnd, rangeStart, selectingEnd]);

  useEffect(() => {
    onHover({ startDate: effectiveStart, endDate: effectiveEnd })
  }, [effectiveStart, effectiveEnd]);

  return (
    <div className="flex flex-col w-[474px] h-auto mt-2 bg-card rounded-lg shadow-lg p-3">
      {/* Header */}
      <div className="h-10 flex items-center justify-between border-b border-neutral-400">
        <div className="flex items-center">
          <Button
            size="icon"
            variant={"text"}
            onClick={handlePrevYear}
          >
            <DoubleLeftOutlined className="h-4 w-4"/>
          </Button>
          <Button
            size="icon"
            variant={"text"}
            onClick={handlePrevMonth}
          >
            <LeftOutlined className="h-4 w-4"/>
          </Button>
        </div>

        <div className="text-sm font-semibold flex-1 text-center">
          {currentYear}/{currentMonth + 1}
        </div>

        <div className="flex items-center gap-2">
          <Button
            size="icon"
            variant={"text"}
            onClick={handleNextMonth}
          >
            <RightOutlined className="h-4 w-4"/>
          </Button>
          <Button
            size="icon"
            variant={"text"}
            onClick={handleNextYear}
          >
            <DoubleRightOutlined className="h-4 w-4"/>
          </Button>
        </div>
      </div>

      <div className={"flex flex-row"}>
        {/* Calendar view */}
        <div className={"w-[282px] px-4 py-2"}>
          <div className="grid grid-cols-7">
            {/* Day names */}
            {dayNames.map((day) => (
              <div
                key={day}
                className="h-[30px] w-9 py-[3px] px-[6px] text-sm text-neutral-900 font-normal"
              >
                {day}
              </div>
            ))}
            {/* Days grid */}
            {days.map(({ date, isCurrentMonth }, index) => {
              const isStart = isSameDay(date, effectiveStart)
              const isEnd = isSameDay(date, effectiveEnd)
              const isInRangeDay = isWithinInterval(date, { start: effectiveStart, end: effectiveEnd })
              const isToday = isSameDay(date, new Date())
              const isSelected = isStart || isEnd
              return (
                <button
                  key={index + "day"}
                  onClick={() => onSelect({ startDate: effectiveStart, endDate: effectiveEnd })}
                  onMouseEnter={() => setHoveredDate(date)}
                  onMouseLeave={() => setHoveredDate(null)}
                  className={cn("h-[30px] w-9 py-[3px] px-[6px] ",
                    isInRangeDay && "bg-accent",
                    isStart && "bg-accent",
                    isEnd && "bg-accent",
                  )}>
                  <div
                    className={cn(
                      "h-6 w-6 rounded-[6px] text-sm font-normal text-neutral-900",
                      !isCurrentMonth && "text-neutral-400",
                      isCurrentMonth && "hover:bg-accent",
                      isToday && !isSelected && "border border-primary",
                      isStart && "bg-purple-200 hover:bg-primary",
                      isEnd && "bg-purple-200 hover:bg-primary",
                    )}
                  >
                    {date.getDate()}
                  </div>
                </button>
              )
            })}
          </div>
        </div>
        <div className={"bg-accent flex-1"}>
          {timePicker}
        </div>
      </div>
    </div>
  )
}
